import { Avatar, Box, Flex, Text } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { Leaderboard } from 'api/generated/graphql';

import { IconTicketTournament } from 'icons';

import { SortingHeader } from 'components/Races2/RacesTable/SortingHeader';
import { PositionTag } from '../Tag';
import Flags, { FlagsProps } from 'components/flags';
import IconSilverTicket from 'components/IconSilverTicket/IconSilverTicket';

export const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const getColumns = (type?: string): ColumnDef<Leaderboard>[] => {
  const baseColumns: ColumnDef<Leaderboard>[] = [
    // Position column
    {
      accessorKey: 'position',
      header: ({ column }) => {
        return (
          <Flex justify="flex-start" align="center" px="2">
            <SortingHeader column={column}>Rank</SortingHeader>
          </Flex>
        );
      },
      id: 'position',
      size: 84,
      cell: ({ row }) => {
        return (
          <Flex justify="flex-start" align="center" px="2">
            <PositionTag position={row.original.position || 0} />
          </Flex>
        );
      },
    },

    {
      accessorKey: 'points',
      header: ({ column }) => {
        return (
          <Flex justify="flex-start" align="center" px="2">
            <SortingHeader column={column}>Points</SortingHeader>
          </Flex>
        );
      },
      id: 'points',
      size: 84,
      cell: ({ row }) => {
        return (
          <Flex h="100%" justify="flex-start" align="center" px="2">
            <Text fontSize="18px" lineHeight="24px" fontWeight={600}>
              {row.original?.points}
            </Text>
          </Flex>
        );
      },
    },

    // Second Column -> Driver
    {
      accessorKey: 'car.user.playerName',
      header: ({ column }) => {
        return (
          <Flex w="100%" justify="flex-start" align="center" px="2">
            <SortingHeader column={column}>Driver</SortingHeader>
          </Flex>
        );
      },
      id: 'driver',
      sortingFn: 'alphanumeric',
      size: 428,
      cell: ({ row }) => {
        return (
          <Flex alignItems="center" px="2" minW="200px">
            <Flags
              countryCode={
                (row.original.car?.user
                  ?.countryFlag as FlagsProps['countryCode']) || 'default'
              }
            />
            <Box
              position="relative"
              borderRadius="0.25rem"
              overflow="hidden"
              width="1.5rem"
              height="1.5rem"
              mx="3"
              bg={`linear-gradient(338deg, #E31B23 -8.99%, rgba(227, 27, 35, 0) 50.37%)`}
            >
              <Avatar
                size="full"
                src={
                  row.original.car?.user.avatarId
                    ? `https://models.readyplayer.me/${row.original.car?.user.avatarId}.png?blendShapes[mouthSmile]=0.4&blendShapes[browInnerUp]=0.2`
                    : '/images/home/driver.png'
                }
                position="absolute"
                w="5.25rem"
                h="5.25rem"
                left="-1.75rem"
                top="-0.475rem"
                objectFit="cover"
                objectPosition="center"
              />
            </Box>
            <Text
              fontSize="18px"
              lineHeight="24px"
              fontWeight={600}
              noOfLines={{ base: 1, md: 2 }}
            >
              {row.original.car?.user?.playerName || 'Owner Name'}
            </Text>
          </Flex>
        );
      },
    },

    // Third Column -> Car
    {
      accessorKey: 'car.name',
      header: ({ column }) => {
        return (
          <Flex w="100%" justify="flex-start" align="center" px="2">
            <SortingHeader column={column}>Car</SortingHeader>
          </Flex>
        );
      },
      id: 'car',
      size: 428,
      sortingFn: 'alphanumeric',
      cell: ({ row }) => {
        return (
          <Flex h="100%" justify="flex-start" align="center" px="2">
            <Text fontSize="18px" lineHeight="24px" fontWeight={600}>
              {row.original?.car?.name}
            </Text>
          </Flex>
        );
      },
    },

    // Fifth Column -> Earnings
    {
      accessorKey: 'earnings',
      header: ({ column }) => {
        return (
          <Flex justify="flex-end" align="center" px="2">
            <SortingHeader column={column}>Earnings</SortingHeader>
          </Flex>
        );
      },
      enableSorting: true,
      id: 'earnings',
      size: 140,
      cell: ({ row }) => {
        return (
          <Flex h="100%" justify="flex-end" align="flex-end" px="2">
            <Text
              fontSize="18px"
              lineHeight="24px"
              color="treasuredTeal.100"
              textAlign="right"
              w="100%"
              fontWeight={600}
            >
              {formatter.format((row.original.earnings || 0) / 100)}
            </Text>
          </Flex>
        );
      },
    },
  ];

  const rewardsColumn: ColumnDef<Leaderboard> = {
    accessorKey: 'position',
    header: ({ column }) => {
      return (
        <Flex w="100%" justify="flex-end" align="center" px="2">
          <SortingHeader column={column}>Rewards</SortingHeader>
        </Flex>
      );
    },
    id: 'position',
    size: 115,
    cell: ({ row }) => {
      const isTopTwenty =
        row?.original?.position && row.original.position <= 20;

      return (
        <Flex justify="flex-end" align="center" px="2" gap="0.25rem">
          {isTopTwenty && <IconTicketTournament height="2rem" width="2rem" />}
          <Text fontSize="18px" lineHeight="24px" fontWeight={600}>
            {isTopTwenty ? '1' : '--'}
          </Text>
        </Flex>
      );
    },
  };

  return type === 'weekly' ? [...baseColumns, rewardsColumn] : baseColumns;
};

// Export getColumns instead of columns
export { getColumns as columns };
